import React, { useEffect, useState } from 'react';
import { Modal, Button, Alert, Form } from 'react-bootstrap';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

// Archive download remains optional, per the owner's earlier decision.
export default function DeleteCompanyModal({ show, onHide, companyId, companyName, onDeleted }) {
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exported, setExported] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        setConfirmText(''); setBusy(false); setExporting(false); setExported(false); setError('');
    }, [show, companyId]);
    const canDelete = Boolean(companyName) && confirmText === companyName && !busy && !exporting;
    const handleExport = async () => {
        setExporting(true); setError('');
        try {
            const response = await axiosInstance.get(getApiUrl(`${companyId}/offboarding/export`), { responseType: 'blob', timeout: 300000 });
            const url = URL.createObjectURL(response.data);
            const anchor = document.createElement('a');
            anchor.href = url; anchor.download = `ledgerloop-arkiv-${companyId}.zip`;
            anchor.click(); URL.revokeObjectURL(url); setExported(true);
        } catch {
            setError('Arkivet kunde inte skapas. Försök igen eller kontakta oss för hjälp med exporten. Företaget har inte stängts.');
        } finally { setExporting(false); }
    };
    const handleDelete = async () => {
        if (!canDelete) return;
        setBusy(true); setError('');
        try {
            const { data } = await axiosInstance.delete(getApiUrl(`auth/company/${companyId}`));
            if (!data.closure_id || !data.companyClosed) throw new Error('Closure receipt missing');
            onDeleted?.(companyId, data); onHide?.();
        } catch (failure) {
            setError(failure?.response?.data?.code === 'RECENT_LOGIN_REQUIRED'
                ? 'Logga ut och logga in igen innan du avslutar företaget.'
                : failure?.response?.data?.message || 'Avslutet kunde inte bekräftas. Kontrollera Företagsavslut innan du försöker igen.');
        } finally { setBusy(false); }
    };
    return <Modal show={show} onHide={busy || exporting ? undefined : onHide} size="lg">
        <Modal.Header closeButton={!busy && !exporting}><Modal.Title>Avsluta {companyName}</Modal.Title></Modal.Header>
        <Modal.Body>
            <Alert variant="warning">Åtkomsten till företaget stängs direkt. Databasen och dokumenten rensas i flera steg som inte kan ångras.
                Vissa kopior kan behöva bevaras eller handläggas. Ditt konto och dina andra företag påverkas inte.</Alert>
            <p>Företaget ansvarar för att behålla de handlingar som behöver sparas. Ladda gärna ner arkivet och kontrollera innehållet innan du fortsätter.
                Nedladdning är frivillig och avslutet kräver inte ett arkiv.</p>
            <Button variant="outline-primary" onClick={handleExport} disabled={busy || exporting}>
                {exporting ? 'Skapar arkiv…' : exported ? 'Ladda ner arkivet igen' : 'Ladda ner arkiv'}
            </Button>
            {exported && <p className="mt-2">Nedladdningen har startats. Kontrollera att filen går att öppna och innehåller dina underlag.</p>}
            <Form.Group className="mt-4" controlId="company-closure-confirm">
                <Form.Label>Skriv företagsnamnet för att bekräfta</Form.Label>
                <Form.Control value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={companyName} disabled={busy || exporting} />
            </Form.Group>
            <p className="mt-3">Du kan följa rensningen under Företagsavslut efteråt. En mottagen begäran betyder inte att alla kopior redan är raderade.</p>
            {error && <Alert variant="danger">{error}</Alert>}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={busy || exporting}>Avbryt</Button>
            <Button variant="danger" onClick={handleDelete} disabled={!canDelete}>{busy ? 'Stänger…' : 'Avsluta företag'}</Button>
        </Modal.Footer>
    </Modal>;
}
